'use client';

import React, { useState, useMemo } from 'react';
import { cn } from '@/app/lib/utils';
import { Pagination, Typography } from '@workspace/ui/common';
import { CaseStudyCard } from './CaseStudyCard';
import { ICaseStudyItem } from '@workspace/interfaces/case-study.types';
import { CaseStudyGridSkeleton } from '@workspace/ui/Skeleton/case-studies/CaseStudyGridSkeleton';

export interface CaseStudyGridProps {
  products: ICaseStudyItem[];
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  onProductClick?: (product: ICaseStudyItem) => void;
  onTagClick?: (tag: string) => void;
  className?: string;
  isLoading?: boolean;
  error?: string | null;
  contentAlignment?: 'left' | 'center' | 'right';
  onScrollToTop?: () => void;
}

export const CaseStudyGrid = ({
  products,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 12,
  onPageChange,
  onItemsPerPageChange,
  onProductClick,
  onTagClick,
  className = '',
  isLoading = false,
  error = null,
  contentAlignment = 'left',
  onScrollToTop,
}: CaseStudyGridProps) => {
  const [localCurrentPage, setLocalCurrentPage] = useState(currentPage);

  const memoizedProducts = useMemo(() => products, [products]);

  const shouldShowPagination = useMemo(
    () => memoizedProducts.length > 0 && totalItems > itemsPerPage,
    [memoizedProducts.length, totalItems, itemsPerPage],
  );

  // Generate alignment classes based on contentAlignment prop
  const getAlignmentClasses = () => {
    switch (contentAlignment) {
      case 'center':
        return 'place-items-center';
      case 'right':
        return 'place-items-end';
      case 'left':
      default:
        return 'place-items-start';
    }
  };

  if (isLoading) {
    return (
      <CaseStudyGridSkeleton className={className} showPagination={shouldShowPagination} itemsCount={itemsPerPage} />
    );
  }
  if (error) {
    return (
      <div className={cn('p-8 text-center', className)}>
        <Typography variant="bodyMd">Failed to load content</Typography>
      </div>
    );
  }

  return (
    <div className={cn('', className)}>
      <div
        className={cn(
          'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-5 xl:gap-x-5 xl:gap-y-16 place-items-center md:place-items-start xl:place-items-start',
          getAlignmentClasses(),
        )}
      >
        {memoizedProducts.map((product) => (
          <CaseStudyCard key={product._id} product={product} onProductClick={onProductClick} onTagClick={onTagClick} />
        ))}
      </div>

      {shouldShowPagination && (
        <Pagination
          currentPage={localCurrentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => {
            setLocalCurrentPage(page);
            onPageChange?.(page);
          }}
          onItemsPerPageChange={
            onItemsPerPageChange ||
            (() => {
              return;
            })
          }
          variant="jumper"
          className="mt-8 items-center !justify-center max-sm:mb-0 mb-20 gap-2"
          showItemsPerPageSelector={false}
          onScrollToTop={onScrollToTop}
        />
      )}
    </div>
  );
};
